import { getColorByRoofZone } from "__editor/panelsEditor/models/panel";
import { DEBUG } from 'debug';
import { ON_THE_CORNER_COLOR } from "./panelColor";
import { state } from '__common/store';
import {
  exposablePanelTextureLite,
  panelTextureLite,
  pendingRemovalPanelTextureLite,
  _baseBayTextureLite,
  clearGeneratedPanelTexturesLite,
} from './panelTextureLite';

const pendingRemovalTextures: { [textureKey: string]: PIXI.Texture } = {};
const bayTextures: { [textureKey: string]: PIXI.Texture } = {};

export function exposablePanelTexture(
  width: number,
  height: number,
  roofZone: roofZoneNumber,
  nearObstruction: boolean,
  exposed: boolean,
  exposedNeighbour: boolean,
  id: number | string,
  panelConfig: number = undefined,
  skewAffected?: number,
  ) {
  if (state().app && state().app.isUbuilderLite) {
    return exposablePanelTextureLite(width, height, roofZone, nearObstruction, exposed, exposedNeighbour, id, panelConfig, skewAffected);
  }
  const borderSize = 1;
  let borderColor: number;
  if (DEBUG.highlightExposedAndExposedNeighbour) {
    if (exposed === true && exposedNeighbour === true) {
      borderColor = 0xeb4034; // #eb4034
    } else if (exposed === true && exposedNeighbour === false) {
      borderColor = 0x37eb34; // #37eb34
    } else if (exposed === false && exposedNeighbour === true) {
      borderColor = 0x16b6fa; // #16b6fa
    } else if (exposed === false && exposedNeighbour === false) {
      borderColor = 0x000000; // #000000
    }
  } else {
    borderColor = 0x000000;
  }
  const COLOR_DARK_GREY = 0xA9A9A9;
  const COLOR_BLACK = 0x000000;

  return _basePanelTexture({
    width,
    height,
    backgroundColor: getColorByRoofZone(roofZone, nearObstruction, skewAffected),
    border: (exposed || exposedNeighbour ?
      {
        color: COLOR_DARK_GREY,
        width: borderSize,
      }: {
        color: COLOR_BLACK,
        width: borderSize,
      }
    ),
    text: id ? String(id) : null,
    panelConfig,
  });
}

export function panelTexture(
  width: number,
  height: number,
  roofZone: roofZoneNumber,
  nearObstruction: boolean,
  id: number | string,
  attached: boolean = false,
  isRMGridflex: boolean = false,
  panelConfig: number = undefined,
  skewAffected?: number,
  ) {
  if (state().app && state().app.isUbuilderLite) {
    return panelTextureLite(width, height, roofZone, nearObstruction, id, attached, isRMGridflex, panelConfig, skewAffected);
  }
  const borderSize = 2;
  const COLOR_BLACK = 0x000000;

  return _basePanelTexture({
    width,
    height,
    backgroundColor: (isRMGridflex && !!attached? ON_THE_CORNER_COLOR: getColorByRoofZone(roofZone, nearObstruction, skewAffected)),
    border: {
      color: COLOR_BLACK,
      width: borderSize,
    },
    text: id ? String(id) : null,
    panelConfig,
  });
}

// Lite-only entry point. Legacy panel rendering never sets pendingRemoval, so
// this delegates straight to the Lite implementation rather than carrying a
// no-op legacy body.
export function pendingRemovalPanelTexture(
  width: number,
  height: number,
  panelConfig: number = undefined,
) {
  if (state().app && state().app.isUbuilderLite) {
    return pendingRemovalPanelTextureLite(width, height, panelConfig);
  }
  const textureKey = `${width}-${height}-${panelConfig ?? ''}`;

  if (pendingRemovalTextures[textureKey]) {
    return pendingRemovalTextures[textureKey];
  }

  pendingRemovalTextures[textureKey] = _basePanelTexture({
    width,
    height,
    backgroundColor: 0xDC2626,
    border: {
      color: 0x7F1D1D,
      width: 2,
    },
    panelConfig,
  });

  return pendingRemovalTextures[textureKey];
}

interface PanelTextureConfig {
  width: number;
  height: number;
  backgroundColor: number;
  text?: string;
  border: {
    color: number;
    width: number;
  };
  panelConfig: number;
}

function _basePanelTexture(config: PanelTextureConfig): PIXI.Texture {
  const { backgroundColor, width, height, border: { width: borderWidth, color: borderColor }, panelConfig } = config;
  const panelTexture = new PIXI.Graphics(true);
  const elevated = panelConfig == 1 || panelConfig == 3;
  const moduleWidth = Math.ceil(width * 2);
  const moduleHeight = Math.ceil(height * 2);

  panelTexture.beginFill(backgroundColor);
  // Keep the outline from swallowing the interior marks on small modules.
  panelTexture.lineStyle(elevated ? Math.min(borderWidth, moduleWidth / 20) : borderWidth, borderColor);
  panelTexture.drawRect(0, 0, moduleWidth, moduleHeight);
  panelTexture.endFill();

  if (elevated) {
    const elevatedMarks = new PIXI.Graphics();
    // Match the flyout's 20 x 35 elevated icon: 1px-wide marks at
    // 10% and 20%, each 10px tall, with the second starting 3px lower.
    elevatedMarks.beginFill(0x000000);
    elevatedMarks.drawRect(moduleWidth * 0.10, 0, moduleWidth / 20, moduleHeight * 10 / 35);
    elevatedMarks.drawRect(moduleWidth * 0.20, moduleHeight * 3 / 35, moduleWidth / 20, moduleHeight * 10 / 35);
    elevatedMarks.endFill();
    panelTexture.addChild(elevatedMarks);
  }

  if (config.text) {
    panelTexture.addChild(_panelIdText(0, 0, width, height, config.text));
  }

  if (state().app && state().app.isUbuilderLite) {
    return panelTexture.generateCanvasTexture(PIXI.SCALE_MODES.LINEAR, Math.max(2, window.devicePixelRatio || 1));
  }
  if (elevated) {
    // Module dimensions can be only a few world units. A 2x raster is still
    // tiny at editor zoom levels; give the shorter edge at least 256 pixels.
    const resolution = Math.max(2, window.devicePixelRatio || 1, 256 / Math.max(1, Math.min(moduleWidth, moduleHeight)));
    return panelTexture.generateCanvasTexture(PIXI.SCALE_MODES.LINEAR, resolution);
  }
  return panelTexture.generateCanvasTexture();
}

function _panelIdText(x: number, y: number, width: number, height: number, text?: string) {
  const tooltip: PIXI.Text = new PIXI.Text(text, {
    fontSize: '42px',
  });

  tooltip.x = x;
  tooltip.y = y;
  tooltip.width = width * 2;
  tooltip.height = height * 2;
  tooltip.id = 'panelIdText';
  return tooltip;
}

export function _baseBayTexture(width, height, attached: 0 | 1=0): PIXI.Texture {
  if (state().app && state().app.isUbuilderLite) {
    return _baseBayTextureLite(width, height, attached);
  }
  const textureKey = `${width}-${height}-${attached}`;

  if (bayTextures[textureKey]) {
    return bayTextures[textureKey];
  }
  const panelTexture = new PIXI.Graphics(true);
  const bayColor = attached ? 0xf2682A : 0x2A81BF;
  panelTexture.beginFill(bayColor);
  panelTexture.lineStyle(1, 0x000000);
  panelTexture.drawRect(0, 0, Math.ceil(width * 2), Math.ceil(height * 2));
  panelTexture.endFill();
  if (state().app && state().app.isUbuilderLite) {
    return panelTexture.generateCanvasTexture(PIXI.SCALE_MODES.LINEAR, Math.max(2, window.devicePixelRatio || 1));
  }
  return panelTexture.generateCanvasTexture();
}

// Cleanup hook for Lite-owned texture caches. Called from the shared
// resetEditor path; on legacy the Lite caches are empty so this is a no-op.
export function clearGeneratedPanelTextures() {
  Object.keys(pendingRemovalTextures).forEach((key) => {
    pendingRemovalTextures[key].destroy(true);
    delete pendingRemovalTextures[key];
  });

  Object.keys(bayTextures).forEach((key) => {
    bayTextures[key].destroy(true);
    delete bayTextures[key];
  });

  clearGeneratedPanelTexturesLite();
}
